import {
	deselectTable,
	HallmapTable,
	selectHallmapTables,
	selectTable,
	setConflitedTable,
	setConflitResolvedTable,
} from 'entities/hallmap';
import { Stage } from 'konva/lib/Stage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Game, Table } from 'shared/lib/core';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import { useOrderResources } from './useOrderResources';
import { CanvasGameProps } from '../types';

import { useUpdateHallmapTables } from './useUpdateHallmapTables';
import { MODES } from 'shared/lib/core/constants';
import { useTableOrders } from './useTableOrders';
import { useLayoutItems } from './useLayoutItems';
import { selectBookingApplicationMod, selectOrderBookingData, selectOrderDetail, useOrderInfo } from 'entities/orders';
import { selectMapBackgroundImage } from 'entities/settings/model/selector';
import { Layer } from 'konva/lib/Layer';
import { useBookingReset } from 'features/booking/BookingClientForm';

export const useCanvasGame = ({
	setHallmap,
	mode = 'static',
	order,
	stage,
}: CanvasGameProps) => {
	const dispatch = useAppDispatch();
	const tables = useAppSelector(selectHallmapTables);
	const applictionMod = useAppSelector(selectBookingApplicationMod);
	const background = useAppSelector(selectMapBackgroundImage);
	const orderDetail = useAppSelector(selectOrderDetail);
	const orderBookingData = useAppSelector(selectOrderBookingData);
	const { open, close, setSelectedId, selectedId } = useOrderInfo();

	const isInitHallmap = useRef(false);
	const [game, setGame] = useState<Game | undefined>();

	const { setResourcesByOrder } = useOrderResources(order);
	const { updateLayoutConfig } = useLayoutItems();

	const { fetchTableOrders, clearTableOrders, loadedTable } =
		useTableOrders();

	useUpdateHallmapTables({
		game,
		order,
		mode,
	});

	useEffect(() => {
		if (!selectedId && !loadedTable && mode === MODES.STATIC) {
			game?.deselect();
		}
	}, [loadedTable, mode, game, selectedId]);

	useEffect(() => {
		if (orderDetail?.appointment?.resources && game?.resources){
			const orderTables = orderDetail.appointment.resources.map(item => item.id);
			const mapOrderTables = game.resources.filter(item => orderTables.includes(item.id));

      if (mapOrderTables.length) {
        mapOrderTables.forEach(table => !table.isSelected ? table.select() : null);
      }
		}
		// eslint-disable-next-line
	}, [orderDetail]);

	useEffect(() => {
		game?.toggleDisableTableClick(orderBookingData?.hold_resources)
	}, [game, orderBookingData?.hold_resources]);

	useBookingReset(() => {
		if (mode === MODES.SELECT) {
			game?.deselect();
		}
	})
 

	const createGame = useCallback(
		(stage: Stage, tables: HallmapTable[]) => {
			isInitHallmap.current = true;

			
			const game = new Game({ stage, tables });

			game.setMode(mode);

			if(mode === MODES.SELECT) {
				game.onBeforeSelectTable = () => {
					if (game.hasTableFirstClick === false) {
						game?.deselect();
					}
				}
			}

			game.onSelectTable = (table: Table, selected: boolean, manual: boolean) => {
				if (manual) {
					game.setTableFirstClick();
				}
				dispatch(selectTable({ table, earlySelected: selected, manual }));
			};

			game.onDeselectTable = (table: Table) => {
				dispatch(deselectTable({ table }));
			};

			game.onConflictTable = (table: Table, resolved: boolean) => {
				if (resolved) {
					dispatch(setConflitResolvedTable({ table }));
				} else {
					dispatch(setConflitedTable({ table }));
				}
			};

			if (background) {
				const bgImage = new Image();
				bgImage.src = background;

				game.setBg(bgImage as any)
			}

			setResourcesByOrder(game);
				
			if (mode === MODES.SELECT) {
				game.resources.map(resource => {
					resource.setActive(false);
				});
			}

			if (mode === MODES.STATIC) {
				if (window.matchMedia("(pointer: coarse)").matches) {
					stage.on('touchstart', event => {
						if (event.target instanceof Stage || event.target.parent instanceof Layer) {
              game?.deselect();
							clearTableOrders();
							close();
						}
					});
				} else {
					stage.on('click', event => {
						if (event.target instanceof Stage || event.target.parent instanceof Layer) {
              game?.deselect();
							clearTableOrders();
							close();
						}
					});
				}			

				game.onClickTable = (table: Table) => {
					if (table.order) {
						if (table.isSelected) {
							close();
							clearTableOrders();
							
							return;
						}
						game?.deselect();
						
						open();
						setSelectedId(table.order.id);

						const orderTables = table.order.appointment.resources.map(item => item.id);
						const mapOrderTables = game.resources.filter(item => orderTables.includes(item.id));

						mapOrderTables.forEach(table => table.select())

						return;
					}

					close();

					if (table.isSelected) {
						clearTableOrders();

						return;
					}

					game?.deselect();
					table.select();
					fetchTableOrders(table);
				};

				game.onDblClickTable = (table: Table) => {
					close()
					if (table.isSelected) {
						clearTableOrders();

						return;
					}

					game?.deselect();
					table.select();
					fetchTableOrders(table);
				};
			}

			if (mode === MODES.EDITOR) {
				game.onMoveTable = (table: Table) => {
					updateLayoutConfig(table);
				};
			}

			setHallmap(game);
			setGame(game);
		},
		[
			updateLayoutConfig,
			mode,
			setHallmap,
			dispatch,
			setResourcesByOrder,
			fetchTableOrders,
			clearTableOrders,
			background,
			setSelectedId,
			close,
			open
		],
	);

	useEffect(() => {
		if (tables.length && stage && !isInitHallmap.current) {
			if (!applictionMod) {
				createGame(stage, tables);
			}
		}
	}, [tables, stage, isInitHallmap, applictionMod, createGame]);

	return { game, createGame };
};
